import React from 'react';
import { PasswordInput, TextInput } from '@mantine/core';
import { Button } from '../../../components/Button';
import type { TenantView } from '../../../services/tenants/tenants.service';
import { noSelectionTextInputProps } from '../../PosShellPage/components/receipt/receipt-input.utils';
import classes from '../tenants-page.module.scss';

export type TenbisDraft = { userName: string; password: string; resId: string };

type TenantTenbisBlockProps = {
  tenant: TenantView;
  isUpdating: boolean;
  draft: TenbisDraft;
  onDraftChange: (patch: Partial<TenbisDraft>) => void;
  onTestLogin: () => void;
  onDisconnect: () => void;
  onSyncMenu: () => void;
};

const formatStamp = (iso: string | null | undefined): string => {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : ` · ${date.toLocaleString('he-IL')}`;
};

const resolveStatus = (tenant: TenantView): string => {
  if (!tenant.tenbisConfigured) {
    return 'לא מוגדר';
  }
  if (tenant.tenbisAuthFailedAt) {
    return `אימות נכשל${formatStamp(tenant.tenbisAuthFailedAt)}`;
  }
  if (tenant.tenbisConnectedAt) {
    return `מחובר${tenant.tenbisResName ? ` · ${tenant.tenbisResName}` : ''}${formatStamp(tenant.tenbisConnectedAt)}`;
  }
  return 'מוגדר · טרם אומת';
};

// Super-admin Tenbis block: creds drafts (saved by שמור הכל), status, test-login, disconnect.
export const TenantTenbisBlock: React.FC<TenantTenbisBlockProps> = ({
  tenant,
  isUpdating,
  draft,
  onDraftChange,
  onTestLogin,
  onDisconnect,
  onSyncMenu,
}) => {
  const configured = Boolean(tenant.tenbisConfigured);
  const statusClass = configured && !tenant.tenbisAuthFailedAt ? classes.tenbisStatusConnected : classes.tenbisStatusDisconnected;
  return (
    <div className={classes.tenbisSlot} data-testid={`tenants-tenbis-block-${tenant.id}`}>
      <span className={classes.photoLabel}>תן ביס</span>
      <div className={classes.settingsGrid}>
        <TextInput
          label="שם משתמש תן ביס"
          {...noSelectionTextInputProps}
          value={draft.userName}
          onChange={(event) => onDraftChange({ userName: event.currentTarget.value })}
          data-testid={`tenants-tenbis-user-${tenant.id}`}
        />
        <PasswordInput
          label="סיסמת תן ביס"
          placeholder={configured ? 'מוגדרת' : 'לא מוגדרת'}
          {...noSelectionTextInputProps}
          value={draft.password}
          onChange={(event) => onDraftChange({ password: event.currentTarget.value })}
          data-testid={`tenants-tenbis-password-${tenant.id}`}
        />
        <TextInput
          label="מזהה מסעדה (resId)"
          inputMode="numeric"
          {...noSelectionTextInputProps}
          value={draft.resId}
          onChange={(event) => onDraftChange({ resId: event.currentTarget.value })}
          data-testid={`tenants-tenbis-res-id-${tenant.id}`}
        />
      </div>
      <div className={classes.tenbisActions}>
        <span className={statusClass} data-testid={`tenants-tenbis-status-${tenant.id}`}>
          {resolveStatus(tenant)}
        </span>
        {configured && (
          <>
            <Button
              variant="save"
              size="sm"
              disabled={isUpdating}
              onClick={onTestLogin}
              dataTestId={`tenants-tenbis-test-login-${tenant.id}`}
            >
              בדוק התחברות
            </Button>
            <Button
              variant="save"
              size="sm"
              disabled={isUpdating || Boolean(tenant.tenbisAuthFailedAt)}
              onClick={onSyncMenu}
              dataTestId={`tenants-tenbis-menu-sync-${tenant.id}`}
            >
              סנכרן תפריט
            </Button>
            <Button
              variant="cancel"
              size="sm"
              disabled={isUpdating}
              onClick={onDisconnect}
              dataTestId={`tenants-tenbis-disconnect-${tenant.id}`}
            >
              נתק
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
